import { useState, useRef, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Search, ScanBarcode, ArrowLeft, Save, Plus, Copy } from "lucide-react";
import { API } from "@/apiConfig";

export default function PriceCheckDialog({
  open, onOpenChange, products,
  categories, subCategories, groups, vendors,
  onCategoriesChange, onSubCategoriesChange, onGroupsChange, onVendorsChange,
  onProductsChange,
}) {
  const sortedCategories = useMemo(() => [...categories].sort((a, b) => a.name.localeCompare(b.name)), [categories]);
  const sortedGroups = useMemo(() => [...groups].sort((a, b) => a.name.localeCompare(b.name)), [groups]);
  const sortedVendors = useMemo(() => [...vendors].sort((a, b) => (a.company_name || "").localeCompare(b.company_name || "", undefined, { sensitivity: 'base' })), [vendors]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [query, setQuery] = useState("");
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [duplicateMode, setDuplicateMode] = useState(false);
  const [duplicateSku, setDuplicateSku] = useState("");
  const inputRef = useRef(null);

  // New entity inputs
  const [newCatName, setNewCatName] = useState("");
  const [newSubCatName, setNewSubCatName] = useState("");
  const [newGroupName, setNewGroupName] = useState("");
  const [newVendorName, setNewVendorName] = useState("");

  useEffect(() => {
    if (open) {
      setQuery(""); setSelectedProduct(null); setEditForm(null);
      setDuplicateMode(false); setDuplicateSku("");
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  const filtered = useMemo(() => {
    if (query.length < 4) return [];
    const q = query.toLowerCase();
    const sw = products.filter(p => p.name?.toLowerCase().startsWith(q));
    const swIds = new Set(sw.map(p => p.id));
    const ct = products.filter(p => !swIds.has(p.id) && p.name?.toLowerCase().includes(q));
    return [...sw, ...ct].slice(0, 100);
  }, [query, products]);

  const handleScan = (e) => {
    if (e.key !== "Enter" || !query.trim()) return;
    // Try exact SKU match first (case-insensitive)
    const skuMatch = products.find(p => p.sku?.toLowerCase() === query.trim().toLowerCase());
    if (skuMatch) { selectProduct(skuMatch); return; }
    // Try alt_skus
    const altMatch = products.find(p => p.alt_skus?.some(a => a.toLowerCase() === query.trim().toLowerCase()));
    if (altMatch) { selectProduct(altMatch); return; }
    // If 4+ chars, let the name search handle it
    if (query.trim().length < 4) toast.error("Product not found");
  };

  const selectProduct = (product) => {
    setSelectedProduct(product);
    const gp = (product.cost_price && product.retail_price && product.retail_price > 0)
      ? (((product.retail_price - product.cost_price) / product.retail_price) * 100).toFixed(1) : "";
    setEditForm({
      name: product.name || "",
      retail_price: product.retail_price?.toString() || "",
      cost_price: product.cost_price?.toString() || "",
      gp_percent: gp,
      tax_1: product.tax_1 || false,
      foodstamp: product.foodstamp || false,
      category: product.category || "",
      sub_category: product.sub_category || "",
      group: product.group || "",
      vendor_ids: product.vendor_ids || [],
    });
    setDuplicateMode(false);
    setDuplicateSku("");
  };

  const handleSave = async () => {
    if (!selectedProduct) return;
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/products/${selectedProduct.id}`, {
        name: editForm.name,
        retail_price: parseFloat(editForm.retail_price) || 0,
        cost_price: parseFloat(editForm.cost_price) || 0,
        tax_1: editForm.tax_1,
        foodstamp: editForm.foodstamp,
        category: editForm.category,
        sub_category: editForm.sub_category,
        group: editForm.group,
        vendor_ids: editForm.vendor_ids,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${editForm.name} updated`);
      Object.assign(selectedProduct, {
        name: editForm.name,
        retail_price: parseFloat(editForm.retail_price) || 0,
        cost_price: parseFloat(editForm.cost_price) || 0,
        tax_1: editForm.tax_1,
        foodstamp: editForm.foodstamp,
        category: editForm.category,
        sub_category: editForm.sub_category,
        group: editForm.group,
        vendor_ids: editForm.vendor_ids,
      });
    } catch {
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicate = () => {
    setDuplicateMode(true);
    setDuplicateSku("");
  };

  const handleDuplicateSave = async () => {
    if (!duplicateSku.trim()) { toast.error("Please enter a SKU"); return; }
    // Check if SKU already exists
    const exists = products.find(p => p.sku?.toLowerCase() === duplicateSku.trim().toLowerCase());
    if (exists) { toast.error("SKU already exists"); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const newProduct = {
        sku: duplicateSku.trim(),
        name: editForm.name,
        retail_price: parseFloat(editForm.retail_price) || 0,
        cost_price: parseFloat(editForm.cost_price) || 0,
        tax_1: editForm.tax_1,
        foodstamp: editForm.foodstamp,
        category: editForm.category,
        sub_category: editForm.sub_category,
        group: editForm.group,
        vendor_ids: editForm.vendor_ids,
      };
      const res = await axios.post(`${API}/products`, newProduct, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Duplicated as "${editForm.name}" with SKU ${duplicateSku.trim()}`);
      if (onProductsChange) onProductsChange();
      setDuplicateMode(false);
      setDuplicateSku("");
      // Select the newly created product
      if (res.data?.id) {
        selectProduct({ ...newProduct, id: res.data.id });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create duplicate");
    } finally {
      setSaving(false);
    }
  };

  const createNewCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/categories`, { name: newCatName.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      onCategoriesChange([...categories, res.data]);
      setEditForm(prev => ({ ...prev, category: res.data.id }));
      setNewCatName("");
      toast.success(`Category "${newCatName.trim()}" created`);
    } catch { toast.error("Failed to create category"); }
  };

  const createNewSubCategory = async () => {
    if (!newSubCatName.trim() || !editForm.category) return;
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/sub-categories`, { name: newSubCatName.trim(), category_id: editForm.category }, { headers: { Authorization: `Bearer ${token}` } });
      onSubCategoriesChange([...subCategories, res.data]);
      setEditForm(prev => ({ ...prev, sub_category: res.data.id }));
      setNewSubCatName("");
      toast.success(`Sub-category "${newSubCatName.trim()}" created`);
    } catch { toast.error("Failed to create sub-category"); }
  };

  const createNewGroup = async () => {
    if (!newGroupName.trim()) return;
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/groups`, { name: newGroupName.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      onGroupsChange([...groups, res.data]);
      setEditForm(prev => ({ ...prev, group: res.data.id }));
      setNewGroupName("");
      toast.success(`Group "${newGroupName.trim()}" created`);
    } catch { toast.error("Failed to create group"); }
  };

  const createNewVendor = async () => {
    if (!newVendorName.trim()) return;
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/vendors`, { company_name: newVendorName.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      onVendorsChange([...vendors, res.data]);
      setEditForm(prev => ({ ...prev, vendor_ids: [res.data.id] }));
      setNewVendorName("");
      toast.success(`Vendor "${newVendorName.trim()}" created`);
    } catch { toast.error("Failed to create vendor"); }
  };

  const catName = (id) => categories.find(c => c.id === id)?.name || "-";
  const subCatName = (id) => subCategories.find(s => s.id === id)?.name || "-";
  const groupName = (id) => groups.find(g => g.id === id)?.name || "-";
  const vendorName = (ids) => ids?.length ? vendors.find(v => v.id === ids[0])?.company_name || "-" : "-";

  // Detail/Edit view
  if (selectedProduct && editForm) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <button onClick={() => { setSelectedProduct(null); setEditForm(null); setDuplicateMode(false); }} className="p-1 hover:bg-zinc-100 rounded">
                <ArrowLeft className="w-4 h-4" />
              </button>
              {duplicateMode ? "Duplicate Product" : `Price Check — ${selectedProduct.name}`}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            {/* SKU Display / Duplicate SKU Input */}
            <div className="bg-zinc-50 rounded-lg p-3 flex items-center justify-between">
              <span className="text-sm text-zinc-500">SKU</span>
              {duplicateMode ? (
                <Input value={duplicateSku} onChange={(e) => setDuplicateSku(e.target.value)}
                  placeholder="Enter new SKU..." className="w-60 h-8 text-right font-mono font-bold"
                  autoFocus data-testid="pc-duplicate-sku" />
              ) : (
                <span className="font-mono font-bold">{selectedProduct.sku}</span>
              )}
            </div>

            {/* Editable Name */}
            <div>
              <Label className="text-xs">Item Name</Label>
              <Input value={editForm.name}
                onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                className="h-9 mt-1" data-testid="pc-name" />
            </div>

            {/* Price & Cost & GP% */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Price</Label>
                <Input type="number" step="0.01" value={editForm.retail_price}
                  onChange={(e) => {
                    const val = e.target.value;
                    setEditForm(prev => {
                      const u = { ...prev, retail_price: val };
                      if (prev.cost_price && val) { const r = parseFloat(val), c = parseFloat(prev.cost_price); if (r > 0 && c >= 0) u.gp_percent = (((r - c) / r) * 100).toFixed(1); }
                      return u;
                    });
                  }}
                  className="h-9 mt-1 font-mono" data-testid="pc-price" />
              </div>
              <div>
                <Label className="text-xs">Cost</Label>
                <Input type="number" step="0.01" value={editForm.cost_price}
                  onChange={(e) => {
                    const val = e.target.value;
                    setEditForm(prev => {
                      const u = { ...prev, cost_price: val };
                      if (val && prev.gp_percent) { const c = parseFloat(val), g = parseFloat(prev.gp_percent); if (c > 0 && g >= 0 && g < 100) u.retail_price = (c / (1 - g / 100)).toFixed(2); }
                      else if (prev.retail_price && val) { const r = parseFloat(prev.retail_price), c = parseFloat(val); if (r > 0 && c >= 0) u.gp_percent = (((r - c) / r) * 100).toFixed(1); }
                      return u;
                    });
                  }}
                  className="h-9 mt-1 font-mono" data-testid="pc-cost" />
              </div>
              <div>
                <Label className="text-xs">GP%</Label>
                <Input type="number" step="0.1" value={editForm.gp_percent || ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    setEditForm(prev => {
                      const u = { ...prev, gp_percent: val };
                      if (prev.cost_price && val) { const c = parseFloat(prev.cost_price), g = parseFloat(val); if (c > 0 && g >= 0 && g < 100) u.retail_price = (c / (1 - g / 100)).toFixed(2); }
                      return u;
                    });
                  }}
                  className="h-9 mt-1 bg-blue-50/50" data-testid="pc-gp" />
              </div>
            </div>

            {/* Tax1 & FS Toggles */}
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg">
                <Label className="text-sm">Tax 1</Label>
                <Switch checked={editForm.tax_1} onCheckedChange={(v) => setEditForm(prev => ({ ...prev, tax_1: v }))} />
              </div>
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <Label className="text-sm">Food Stamp</Label>
                <Switch checked={editForm.foodstamp} onCheckedChange={(v) => setEditForm(prev => ({ ...prev, foodstamp: v }))} />
              </div>
            </div>

            {/* Category */}
            <div>
              <Label className="text-xs">Category</Label>
              {newCatName !== null && editForm.category === "__new__" ? (
                <div className="flex gap-1 mt-1">
                  <Input autoFocus placeholder="New category name..." value={newCatName}
                    onChange={(e) => setNewCatName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") createNewCategory(); if (e.key === "Escape") { setNewCatName(""); setEditForm(prev => ({ ...prev, category: "" })); } }}
                    className="flex-1 h-9 text-sm" />
                  <Button size="sm" className="h-9 px-3" onClick={createNewCategory} disabled={!newCatName.trim()}>Add</Button>
                  <Button size="sm" variant="outline" className="h-9 px-2" onClick={() => { setNewCatName(""); setEditForm(prev => ({ ...prev, category: "" })); }}>X</Button>
                </div>
              ) : (
                <select value={editForm.category}
                  onChange={(e) => { if (e.target.value === "__new__") { setNewCatName(""); } setEditForm(prev => ({ ...prev, category: e.target.value, sub_category: "" })); }}
                  className="w-full h-9 px-2 border rounded-md text-sm mt-1" data-testid="pc-category">
                  <option value="">Select</option>
                  {sortedCategories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  <option value="__new__">+ Add New Category...</option>
                </select>
              )}
            </div>

            {/* Sub Category */}
            <div>
              <Label className="text-xs">Sub Category</Label>
              {newSubCatName !== null && editForm.sub_category === "__new__" ? (
                <div className="flex gap-1 mt-1">
                  <Input autoFocus placeholder="New sub-category name..." value={newSubCatName}
                    onChange={(e) => setNewSubCatName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") createNewSubCategory(); if (e.key === "Escape") { setNewSubCatName(""); setEditForm(prev => ({ ...prev, sub_category: "" })); } }}
                    className="flex-1 h-9 text-sm" />
                  <Button size="sm" className="h-9 px-3" onClick={createNewSubCategory} disabled={!newSubCatName.trim() || !editForm.category}>Add</Button>
                  <Button size="sm" variant="outline" className="h-9 px-2" onClick={() => { setNewSubCatName(""); setEditForm(prev => ({ ...prev, sub_category: "" })); }}>X</Button>
                </div>
              ) : (
                <select value={editForm.sub_category}
                  onChange={(e) => { if (e.target.value === "__new__") { setNewSubCatName(""); } setEditForm(prev => ({ ...prev, sub_category: e.target.value })); }}
                  className="w-full h-9 px-2 border rounded-md text-sm mt-1" data-testid="pc-subcategory"
                  disabled={!editForm.category}>
                  <option value="">Select</option>
                  {[...subCategories.filter(s => s.category_id === editForm.category)].sort((a, b) => a.name.localeCompare(b.name)).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  {editForm.category && <option value="__new__">+ Add New Sub-Category...</option>}
                </select>
              )}
            </div>

            {/* Group */}
            <div>
              <Label className="text-xs">Group</Label>
              {newGroupName !== null && editForm.group === "__new__" ? (
                <div className="flex gap-1 mt-1">
                  <Input autoFocus placeholder="New group name..." value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") createNewGroup(); if (e.key === "Escape") { setNewGroupName(""); setEditForm(prev => ({ ...prev, group: "" })); } }}
                    className="flex-1 h-9 text-sm" />
                  <Button size="sm" className="h-9 px-3" onClick={createNewGroup} disabled={!newGroupName.trim()}>Add</Button>
                  <Button size="sm" variant="outline" className="h-9 px-2" onClick={() => { setNewGroupName(""); setEditForm(prev => ({ ...prev, group: "" })); }}>X</Button>
                </div>
              ) : (
                <select value={editForm.group}
                  onChange={(e) => { if (e.target.value === "__new__") { setNewGroupName(""); } setEditForm(prev => ({ ...prev, group: e.target.value })); }}
                  className="w-full h-9 px-2 border rounded-md text-sm mt-1" data-testid="pc-group">
                  <option value="">Select</option>
                  {sortedGroups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                  <option value="__new__">+ Add New Group...</option>
                </select>
              )}
            </div>

            {/* Vendor */}
            <div>
              <Label className="text-xs">Vendor</Label>
              {newVendorName !== null && (editForm.vendor_ids[0] || "") === "__new__" ? (
                <div className="flex gap-1 mt-1">
                  <Input autoFocus placeholder="New vendor name..." value={newVendorName}
                    onChange={(e) => setNewVendorName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") createNewVendor(); if (e.key === "Escape") { setNewVendorName(""); setEditForm(prev => ({ ...prev, vendor_ids: [] })); } }}
                    className="flex-1 h-9 text-sm" />
                  <Button size="sm" className="h-9 px-3" onClick={createNewVendor} disabled={!newVendorName.trim()}>Add</Button>
                  <Button size="sm" variant="outline" className="h-9 px-2" onClick={() => { setNewVendorName(""); setEditForm(prev => ({ ...prev, vendor_ids: [] })); }}>X</Button>
                </div>
              ) : (
                <select value={editForm.vendor_ids[0] || ""}
                  onChange={(e) => { if (e.target.value === "__new__") { setNewVendorName(""); } setEditForm(prev => ({ ...prev, vendor_ids: e.target.value && e.target.value !== "__new__" ? [e.target.value] : e.target.value === "__new__" ? ["__new__"] : [] })); }}
                  className="w-full h-9 px-2 border rounded-md text-sm mt-1" data-testid="pc-vendor">
                  <option value="">Select</option>
                  {sortedVendors.map(v => <option key={v.id} value={v.id}>{v.company_name}</option>)}
                  <option value="__new__">+ Add New Vendor...</option>
                </select>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 mt-2">
            <Button variant="outline" className="flex-1" onClick={() => { setSelectedProduct(null); setEditForm(null); setDuplicateMode(false); }}>
              Back
            </Button>
            {duplicateMode ? (
              <Button className="flex-1 bg-green-600 hover:bg-green-700" onClick={handleDuplicateSave} disabled={saving || !duplicateSku.trim()} data-testid="pc-duplicate-save-btn">
                <Save className="w-4 h-4 mr-1" />
                {saving ? "Creating..." : "Create Duplicate"}
              </Button>
            ) : (
              <>
                <Button variant="outline" className="text-blue-600 border-blue-200 hover:bg-blue-50" onClick={handleDuplicate} data-testid="pc-duplicate-btn">
                  <Copy className="w-4 h-4 mr-1" />
                  Duplicate
                </Button>
                <Button className="flex-1 bg-blue-600 hover:bg-blue-700" onClick={handleSave} disabled={saving} data-testid="pc-save-btn">
                  <Save className="w-4 h-4 mr-1" />
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Search view
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScanBarcode className="w-5 h-5" />
            Price Check
          </DialogTitle>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input
            ref={inputRef}
            placeholder="Scan SKU or type product name (4+ chars)..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleScan}
            className="pl-10 h-11 text-base"
            data-testid="pricecheck-search-input"
          />
        </div>

        {query.length > 0 && query.length < 4 && (
          <p className="text-sm text-zinc-400 text-center py-2">Scan a barcode or type 4+ characters</p>
        )}

        {query.length >= 4 && filtered.length === 0 && (
          <p className="text-sm text-zinc-500 text-center py-4">No products found</p>
        )}

        {filtered.length > 0 && (
          <ScrollArea className="max-h-[500px]">
            <div className="space-y-1">
              {filtered.map((product) => (
                <button
                  key={product.id}
                  onClick={() => selectProduct(product)}
                  className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-blue-50 border border-transparent hover:border-blue-200 transition-all text-left"
                  data-testid={`pc-item-${product.sku}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-zinc-900 truncate">{product.name}</p>
                    <div className="flex gap-3 text-xs text-zinc-400 mt-0.5">
                      <span className="font-mono">{product.sku}</span>
                      <span>{catName(product.category)}</span>
                      {product.tax_1 && <span className="text-amber-600">T1</span>}
                      {product.foodstamp && <span className="text-green-600">FS</span>}
                    </div>
                  </div>
                  <span className="text-base font-bold text-blue-600 ml-3 font-mono">
                    ${(product.retail_price || 0).toFixed(2)}
                  </span>
                </button>
              ))}
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
}
